import { Ceil, Floor, Max, Min } from "@pdfking/math"
import { DefaultMinimap as M } from "../../maps/DefaultMinimap"

// WASI/Emscripten errno values
const ENOENT = 44
const EBADF = 8
const EINVAL = 28
const ENOTDIR = 54
const EISDIR = 31

// fcntl commands
const F_DUPFD = 0
const F_GETFD = 1
const F_SETFD = 2
const F_GETFL = 3
const F_SETFL = 4

// open flags
const O_CREAT = 64
const O_TRUNC = 512
const O_APPEND = 1024

// seek whence
const SEEK_SET = 0
const SEEK_CUR = 1
const SEEK_END = 2

type FileEntry = {
  data: Uint8Array
  readonly isDir: boolean
}

type OpenFD = {
  readonly path: string
  flags: number
  offset: number
}

const normalizePath = (path: string): string => {
  if (path.charAt(0) === "/") {
    const parts = path.split("/").filter(Boolean)
    const result: string[] = []
    for (const part of parts) {
      if (part === "..") result.pop()
      else if (part !== ".") result.push(part)
    }
    return `/${result.join("/")}`
  }
  return path
}

type FnRecord = Readonly<
  Record<string, (...args: (number | bigint)[]) => number | bigint | undefined>
>

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type InternalFnRecord = Record<string, (...args: any[]) => number | undefined>

/** Convert all args to number (memory64 passes BigInt for pointer-sized params). */
const N = (v: number | bigint): number => Number(v)

/** Build minified env + wasi import functions backed by an in-memory FS. */
export const BuildFSImports = (
  exports: Record<string, unknown>
): { readonly env: FnRecord; readonly wasi: FnRecord } => {
  const files = new Map<string, FileEntry>()
  const fds = new Map<number, OpenFD>()
  let nextFd = 10

  files.set("/tmp", { data: new Uint8Array(0), isDir: true })
  files.set("/dev", { data: new Uint8Array(0), isDir: true })

  const mem = () => (exports[M.memory] as WebAssembly.Memory).buffer
  const heapU8 = () => new Uint8Array(mem())
  const heapU32 = () => new Uint32Array(mem())
  const heap32 = () => new Int32Array(mem())
  const dv = () => new DataView(mem())

  const getStr = (ptr: number): string => {
    const bytes = heapU8()
    let end = ptr
    while (bytes[end] !== 0) end++
    return new TextDecoder().decode(bytes.subarray(ptr, end))
  }

  const calculateAt = (dirfd: number, path: string): string => {
    if (path.charAt(0) === "/") return normalizePath(path)
    let dir: string
    if (dirfd === -100) {
      dir = "/"
    } else {
      const stream = fds.get(dirfd)
      if (!stream) return normalizePath(`/${path}`)
      dir = stream.path
    }
    if (path.length === 0) return dir
    return normalizePath(`${dir}/${path}`)
  }

  let varargs = 0
  const getVarargI = (): number => {
    const ret = heap32()[varargs >> 2]!
    varargs += 4
    return ret
  }

  const writeStat = (buf: number, entry: FileEntry): number => {
    const h = heapU32()
    const d = dv()
    const now = Date.now()
    const timeSec = BigInt(Floor(now / 1000))
    const timeNsec = (now % 1000) * 1000000

    h[buf >> 2] = 1
    h[(buf + 4) >> 2] = entry.isDir ? 0o40755 : 0o100644
    h[(buf + 8) >> 2] = 1
    h[(buf + 12) >> 2] = 0
    h[(buf + 16) >> 2] = 0
    h[(buf + 20) >> 2] = 0
    d.setBigInt64(buf + 24, BigInt(entry.data.length), true)
    heap32()[(buf + 32) >> 2] = 4096
    heap32()[(buf + 36) >> 2] = Ceil(entry.data.length / 512)
    d.setBigInt64(buf + 40, timeSec, true)
    h[(buf + 48) >> 2] = timeNsec
    d.setBigInt64(buf + 56, timeSec, true)
    h[(buf + 64) >> 2] = timeNsec
    d.setBigInt64(buf + 72, timeSec, true)
    h[(buf + 80) >> 2] = timeNsec
    d.setBigInt64(buf + 88, BigInt(1), true)
    return 0
  }

  const env: InternalFnRecord = {
    [M.__syscall_openat]: (
      dirfd: number,
      pathPtr: number,
      flags: number,
      va: number
    ) => {
      varargs = va
      const mode = va ? getVarargI() : 0
      void mode
      const path = calculateAt(dirfd, getStr(pathPtr))
      const entry = files.get(path)

      if (!entry && !(flags & O_CREAT)) return -ENOENT
      if (!entry) {
        files.set(path, { data: new Uint8Array(0), isDir: false })
      } else if (entry.isDir) {
        return -EISDIR
      } else if (flags & O_TRUNC) {
        entry.data = new Uint8Array(0)
      }

      const fd = nextFd++
      fds.set(fd, {
        path,
        flags,
        offset: flags & O_APPEND ? (files.get(path)?.data.length ?? 0) : 0,
      })
      return fd
    },

    [M.__syscall_stat64]: (pathPtr: number, buf: number) => {
      const path = normalizePath(getStr(pathPtr))
      const entry = files.get(path)
      if (!entry) return -ENOENT
      return writeStat(buf, entry)
    },

    [M.__syscall_fcntl64]: (fd: number, cmd: number, va: number) => {
      varargs = va
      const openFd = fds.get(fd)
      if (!openFd && fd > 2) return -EBADF

      switch (cmd) {
        case F_DUPFD: {
          let arg = getVarargI()
          if (arg < 0) return -EINVAL
          while (fds.has(arg)) arg++
          if (openFd) fds.set(arg, { ...openFd })
          return arg
        }
        case F_GETFD:
        case F_SETFD:
          return 0
        case F_GETFL:
          return openFd?.flags ?? 0
        case F_SETFL: {
          const arg = getVarargI()
          if (openFd) openFd.flags |= arg
          return 0
        }
        default:
          return -EINVAL
      }
    },

    [M.__syscall_ioctl]: () => 0,

    [M.__syscall_unlinkat]: (dirfd: number, pathPtr: number, flags: number) => {
      const path = calculateAt(dirfd, getStr(pathPtr))
      const entry = files.get(path)
      if (!entry) return -ENOENT
      if (!flags) {
        if (entry.isDir) return -EISDIR
        files.delete(path)
      } else if (flags === 512) {
        if (!entry.isDir) return -ENOTDIR
        files.delete(path)
      } else {
        return -EINVAL
      }
      return 0
    },

    [M.__syscall_renameat]: (
      olddirfd: number,
      oldpathPtr: number,
      newdirfd: number,
      newpathPtr: number
    ) => {
      const oldpath = calculateAt(olddirfd, getStr(oldpathPtr))
      const newpath = calculateAt(newdirfd, getStr(newpathPtr))
      const entry = files.get(oldpath)
      if (!entry) return -ENOENT
      files.set(newpath, entry)
      files.delete(oldpath)
      for (const [, openFd] of fds) {
        if (openFd.path === oldpath) {
          ;(openFd as { path: string }).path = newpath
        }
      }
      return 0
    },

    [M.__syscall_fstat64]: (fd: number, buf: number) => {
      if (fd <= 2)
        return writeStat(buf, { data: new Uint8Array(0), isDir: false })
      const openFd = fds.get(fd)
      if (!openFd) return -EBADF
      const entry = files.get(openFd.path)
      if (!entry) return -EBADF
      return writeStat(buf, entry)
    },
  }

  const wasi: InternalFnRecord = {
    [M.fd_close]: (fd: number) => {
      if (!fds.has(fd)) return EBADF
      fds.delete(fd)
      return 0
    },

    [M.fd_read]: (fd: number, iov: number, iovcnt: number, pnum: number) => {
      const openFd = fds.get(fd)
      if (!openFd) return EBADF
      const entry = files.get(openFd.path)
      if (!entry) return EBADF

      const h8 = heapU8()
      const hU32 = heapU32()
      let ret = 0

      for (let i = 0; i < iovcnt; i++) {
        const ptr = hU32[iov >> 2]!
        const len = hU32[(iov + 4) >> 2]!
        iov += 8

        const available = entry.data.length - openFd.offset
        const curr = Min(len, Max(0, available))
        if (curr > 0) {
          h8.set(entry.data.subarray(openFd.offset, openFd.offset + curr), ptr)
          openFd.offset += curr
        }
        ret += curr
        if (curr < len) break
      }

      hU32[pnum >> 2] = ret
      return 0
    },

    [M.fd_write]: (fd: number, iov: number, iovcnt: number, pnum: number) => {
      if (fd === 1 || fd === 2) {
        const hU32 = heapU32()
        let ret = 0
        for (let i = 0; i < iovcnt; i++) {
          const len = hU32[(iov + 4) >> 2]!
          iov += 8
          ret += len
        }
        hU32[pnum >> 2] = ret
        return 0
      }

      const openFd = fds.get(fd)
      if (!openFd) return EBADF
      const entry = files.get(openFd.path)
      if (!entry) return EBADF

      const hU8 = heapU8()
      const hU32 = heapU32()
      let ret = 0

      for (let i = 0; i < iovcnt; i++) {
        const ptr = hU32[iov >> 2]!
        const len = hU32[(iov + 4) >> 2]!
        iov += 8

        const needed = openFd.offset + len
        if (needed > entry.data.length) {
          const newData = new Uint8Array(needed)
          newData.set(entry.data)
          entry.data = newData
        }
        entry.data.set(hU8.subarray(ptr, ptr + len), openFd.offset)
        openFd.offset += len
        ret += len
      }

      hU32[pnum >> 2] = ret
      return 0
    },

    [M.fd_seek]: (
      fd: number,
      offset: number,
      whence: number,
      newOffsetPtr: number
    ) => {
      const openFd = fds.get(fd)
      if (!openFd) return EBADF
      const entry = files.get(openFd.path)
      if (!entry) return EBADF

      const off = Number(offset)
      if (Number.isNaN(off)) return 61

      let pos: number
      if (whence === SEEK_SET) pos = off
      else if (whence === SEEK_CUR) pos = openFd.offset + off
      else if (whence === SEEK_END) pos = entry.data.length + off
      else return EINVAL

      openFd.offset = pos
      dv().setBigInt64(newOffsetPtr, BigInt(pos), true)
      return 0
    },
  }

  /** Wrap all functions to Number()-convert args (memory64 passes BigInt). */
  const wrap = (fns: InternalFnRecord): FnRecord => {
    const r: Record<
      string,
      (...args: (number | bigint)[]) => number | bigint | undefined
    > = {}
    for (const k in fns) r[k] = (...args) => fns[k]!(...args.map(N))
    return r
  }

  return { env: wrap(env), wasi: wrap(wasi) }
}
